import { type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TableCell, TableRow } from '@/components/ui/table';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { InventoryUserListDialog } from '@/pages/organization/components/InventoryUserListDialog';
import {
  listHookGroupProjects,
  listAccountHookGroupProjects,
} from '@kirin/api-client/api/hooks';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/hooks/api/queryKeys';
import { HOOK_STALE_TIME_MS } from './constants';
import { type HookTabScope, hookScopeKey } from './scope';

interface HookProjectsModalProps {
  scope: HookTabScope;
  groupKey: string | null;
  onClose: () => void;
}

export function HookProjectsModal({
  scope,
  groupKey,
  onClose,
}: HookProjectsModalProps): ReactElement {
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: queryKeys.hooks.projects(hookScopeKey(scope), groupKey ?? ''),
    queryFn: () => {
      // `enabled` gates this to groupKey !== null; the guard narrows the type
      // without an assertion (and never throws in practice).
      if (groupKey === null) {
        throw new Error('hook projects query ran without a groupKey');
      }
      return scope.kind === 'organization'
        ? listHookGroupProjects(apiClient, scope.organizationId, groupKey)
        : listAccountHookGroupProjects(apiClient, groupKey);
    },
    enabled: groupKey !== null,
    staleTime: HOOK_STALE_TIME_MS,
  });

  const projects = data?.projects ?? [];

  return (
    <InventoryUserListDialog
      open={groupKey !== null}
      onClose={onClose}
      title={`Projects with this hook (${isLoading ? '...' : projects.length})`}
      description="Projects where this hook was seen."
      columns={[
        { label: 'Project' },
        { label: 'Users', className: 'text-center' },
        { label: 'Last Seen' },
      ]}
      isLoading={isLoading}
      error={isError ? true : null}
      isEmpty={projects.length === 0}
      emptyMessage="No projects found"
      loadingMessage="Loading projects..."
      errorMessage="Failed to load projects. Please try again."
      onRetry={() => void refetch()}
      isRetrying={isFetching}
    >
      {projects.map(project => (
        <TableRow key={project.projectId}>
          <TableCell className="font-medium break-all">
            {project.name}
          </TableCell>
          <TableCell className="text-center">{project.userCount}</TableCell>
          <TableCell>
            <RelativeTimeWithTooltip date={project.lastSeenAt} />
          </TableCell>
        </TableRow>
      ))}
    </InventoryUserListDialog>
  );
}
